/**
 * The evaluator's runtime value model and Excel coercion rules.
 *
 * Three shapes flow through the evaluator:
 *
 * - a **scalar**, which is exactly a stored {@link CellValue} (blank, number,
 *   text, boolean, error),
 * - a **reference** (`RefValue`), a resolved {@link Range} that a function may
 *   read lazily — this is what lets `SUM(A1:A1000000)` avoid materializing a
 *   million objects, and what `ISBLANK`, `OFFSET`, `INDEX`, and `COUNTBLANK`
 *   need in order to tell "blank cell" from "empty string",
 * - an **array** (`ArrayValue`), an in-memory grid produced by the dynamic-array
 *   functions (`UNIQUE`, `SORT`, `FILTER`, `SPLIT`) and by `INDEX` when it is
 *   asked for a whole row or column.
 *
 * Both references and arrays are read through {@link gridOf}, a lazy
 * `rows × cols` accessor, so every function body can be written once against
 * one shape.
 *
 * The coercion rules here are the single source of truth for Requirements 4.8
 * (`#DIV/0!`), 4.10 (`#VALUE!`), and the text/number/boolean conversions the
 * Core_Formula_Set performs. They are pure functions with no I/O, which is what
 * makes Property 13 testable against a naive reference evaluator.
 */

import { MAX_COLS, MAX_ROWS, type Addr, type CellValue, type ErrorCode, type Range, type SheetId } from '../types.js';

/** A single stored value. Identical to {@link CellValue} by design. */
export type Scalar = CellValue;

/** An in-memory grid of scalars, row-major. */
export interface ArrayValue {
  k: 'array';
  rows: number;
  cols: number;
  cells: Scalar[][];
}

/** A resolved reference: a range the function may read cell by cell. */
export interface RefValue {
  k: 'ref';
  range: Range;
}

/** Anything an argument or an operand can be. */
export type Value = Scalar | ArrayValue | RefValue;

/**
 * Everything the evaluator needs from the outside world. Deliberately tiny: a
 * cell reader, three resolvers, and a clock. The Workbook-backed implementation
 * lives in `context.ts`; tests supply their own.
 */
export interface EvalContext {
  /** The cell the formula being evaluated lives in. */
  readonly at: Addr;
  /** Reads one cell. Out-of-data addresses must read as blank, not throw. */
  getCell(a: Addr): CellValue;
  /** Maps a sheet name as written in a formula to a {@link SheetId}. */
  resolveSheet(name: string): SheetId | null;
  /** Resolves a Named_Range (Requirement 4.3). */
  resolveName(name: string): Range | null;
  /** Milliseconds since the Unix epoch, for `TODAY` and `NOW`. */
  now(): number;
  /**
   * Optional: shrinks an over-large range to the sheet's used bounds. Only ever
   * applied to ranges above {@link CLAMP_THRESHOLD_CELLS} cells, so it can never
   * change the shape of a range that holds data.
   */
  clampRange?(r: Range): Range;
}

/** A function's declared contract in the registry (design: `FnSpec`). */
export interface FnSpec {
  /** Upper-case name, as the parser normalizes call names. */
  readonly name: string;
  /** Minimum argument count. */
  readonly min: number;
  /** Maximum argument count; {@link VARIADIC} for open-ended functions. */
  readonly max: number;
  /**
   * When true the function inspects error arguments itself, so the evaluator
   * does not short-circuit on them (`ISERROR`, `IFERROR`, `IFNA`, `IF`, ...).
   */
  readonly errorTolerant?: boolean;
  /** True for functions whose natural result is a grid that spills. */
  readonly array?: boolean;
  /** True for functions that must be recomputed every Recalculation_Cycle. */
  readonly volatile?: boolean;
  /** The implementation. Receives raw values, references included. */
  readonly fn: (args: readonly Value[], ctx: EvalContext) => Value;
}

/** Excel's practical ceiling on argument count, used for variadic functions. */
export const VARIADIC = 255;

/** Cells above which a reference is clamped to the used range before reading. */
export const CLAMP_THRESHOLD_CELLS = 4096;

/** Largest grid a function may materialize, so a bad argument cannot exhaust memory. */
export const MAX_ARRAY_CELLS = 1_048_576;

export const BLANK: Scalar = { k: 'empty' };
export const TRUE: Scalar = { k: 'bool', v: true };
export const FALSE: Scalar = { k: 'bool', v: false };

export function err(code: ErrorCode): Scalar {
  return { k: 'error', v: code };
}

/** Wraps a computed number, mapping non-finite results to `#NUM!`. */
export function num(v: number): Scalar {
  if (Number.isNaN(v)) return err('#NUM!');
  if (!Number.isFinite(v)) return err('#NUM!');
  return { k: 'number', v };
}

export function text(v: string): Scalar {
  return { k: 'text', v };
}

export function bool(v: boolean): Scalar {
  return v ? TRUE : FALSE;
}

export function isScalar(v: Value): v is Scalar {
  return v.k !== 'array' && v.k !== 'ref';
}

export function isError(v: Value): v is { k: 'error'; v: ErrorCode; detail?: string; origin?: Addr } {
  return v.k === 'error';
}

/** The error code of `v` when it is an error scalar, else `null`. */
export function errorOf(v: Value): ErrorCode | null {
  return v.k === 'error' ? v.v : null;
}

/* ------------------------------------------------------------------- grids */

/** A lazily read `rows × cols` grid. 0-based indices. */
export interface Grid {
  readonly rows: number;
  readonly cols: number;
  get(row: number, col: number): Scalar;
  /** The range behind the grid, when it came from a reference. */
  readonly range?: Range;
}

function clampedRange(r: Range, ctx: EvalContext): Range {
  const cells = (r.r1 - r.r0 + 1) * (r.c1 - r.c0 + 1);
  if (cells <= CLAMP_THRESHOLD_CELLS || ctx.clampRange === undefined) return r;
  return ctx.clampRange(r);
}

/** Views any value as a grid. Scalars become 1×1 grids. */
export function gridOf(v: Value, ctx: EvalContext): Grid {
  if (v.k === 'array') {
    return {
      rows: v.rows,
      cols: v.cols,
      get: (row, col) => v.cells[row]?.[col] ?? BLANK,
    };
  }
  if (v.k === 'ref') {
    const range = clampedRange(v.range, ctx);
    const sheet = range.sheet;
    return {
      rows: range.r1 - range.r0 + 1,
      cols: range.c1 - range.c0 + 1,
      get: (row, col) => ctx.getCell({ sheet, row: range.r0 + row, col: range.c0 + col }),
      range,
    };
  }
  const scalar = v;
  return { rows: 1, cols: 1, get: () => scalar };
}

/** Every cell of a grid in row-major order. */
export function cellsOfGrid(grid: Grid): Scalar[] {
  const out: Scalar[] = [];
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) out.push(grid.get(r, c));
  }
  return out;
}

/** Every cell of a value in row-major order. */
export function cellsOf(v: Value, ctx: EvalContext): Scalar[] {
  return cellsOfGrid(gridOf(v, ctx));
}

/** Materializes a grid as an {@link ArrayValue}, or `#NUM!` when it is too big. */
export function arrayOfGrid(grid: Grid): ArrayValue | Scalar {
  if (grid.rows * grid.cols > MAX_ARRAY_CELLS) return err('#NUM!');
  const cells: Scalar[][] = [];
  for (let r = 0; r < grid.rows; r++) {
    const row: Scalar[] = [];
    for (let c = 0; c < grid.cols; c++) row.push(grid.get(r, c));
    cells.push(row);
  }
  return { k: 'array', rows: grid.rows, cols: grid.cols, cells };
}

/** Builds an {@link ArrayValue} from rows of scalars. Rows are padded to equal width. */
export function arrayOf(cells: Scalar[][]): ArrayValue {
  const rows = cells.length;
  let cols = 0;
  for (const row of cells) cols = Math.max(cols, row.length);
  const padded = cells.map((row) => {
    const copy = row.slice();
    while (copy.length < cols) copy.push(BLANK);
    return copy;
  });
  return { k: 'array', rows, cols, cells: padded };
}

/**
 * Collapses a value to a single scalar, the way Excel collapses an operand:
 * a 1×1 grid yields its cell, anything larger is a type error (`#VALUE!`).
 */
export function toScalar(v: Value, ctx: EvalContext): Scalar {
  if (isScalar(v)) return v;
  const grid = gridOf(v, ctx);
  if (grid.rows === 1 && grid.cols === 1) return grid.get(0, 0);
  if (grid.rows === 0 || grid.cols === 0) return err('#REF!');
  return err('#VALUE!');
}

/* --------------------------------------------------------------- coercions */

/** Numeric literal text the way Excel accepts it, with an optional `%` suffix. */
const NUMERIC_TEXT = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * Number coercion (Requirement 4.10). Blank is 0, booleans are 1/0, numeric
 * text parses, anything else is `#VALUE!`. Errors pass through unchanged so a
 * caller can propagate them.
 */
export function toNumber(v: Scalar): number | ErrorCode {
  switch (v.k) {
    case 'empty':
      return 0;
    case 'number':
      return v.v;
    case 'bool':
      return v.v ? 1 : 0;
    case 'error':
      return v.v;
    case 'text':
      return parseNumericText(v.v);
  }
}

/** Parses numeric text, honouring a trailing `%`. `#VALUE!` when it is not a number. */
export function parseNumericText(raw: string): number | ErrorCode {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '#VALUE!';
  const percent = trimmed.endsWith('%');
  const body = percent ? trimmed.slice(0, -1).trim() : trimmed;
  if (!NUMERIC_TEXT.test(body)) return '#VALUE!';
  const parsed = Number(body);
  if (!Number.isFinite(parsed)) return '#VALUE!';
  return percent ? parsed / 100 : parsed;
}

/** Text coercion. Blank is the empty string, booleans are `TRUE`/`FALSE`. */
export function toText(v: Scalar): string | ErrorCode {
  switch (v.k) {
    case 'empty':
      return '';
    case 'number':
      return numberToText(v.v);
    case 'bool':
      return v.v ? 'TRUE' : 'FALSE';
    case 'text':
      return v.v;
    case 'error':
      return v.v;
  }
}

/**
 * Boolean coercion. Blank is false, a number is false only at zero, the words
 * `TRUE`/`FALSE` convert (case-insensitively), other text is `#VALUE!`.
 */
export function toBool(v: Scalar): boolean | ErrorCode {
  switch (v.k) {
    case 'empty':
      return false;
    case 'number':
      return v.v !== 0;
    case 'bool':
      return v.v;
    case 'error':
      return v.v;
    case 'text': {
      const folded = v.v.trim().toUpperCase();
      if (folded === 'TRUE') return true;
      if (folded === 'FALSE') return false;
      return '#VALUE!';
    }
  }
}

/**
 * General-format rendering of a number, used wherever a number is coerced to
 * text. Integers print exactly; other values print at 15 significant digits
 * with trailing zeros removed, which is the precision Excel displays.
 */
export function numberToText(v: number): string {
  if (!Number.isFinite(v)) return '#NUM!';
  if (Object.is(v, -0)) return '0';
  if (Number.isInteger(v) && Math.abs(v) < 1e15) return String(v);
  const precise = v.toPrecision(15);
  if (precise.includes('e') || precise.includes('E')) return String(v);
  const trimmed = precise.replace(/0+$/, '').replace(/\.$/, '');
  return Number(trimmed) === v ? trimmed : String(v);
}

/** True for a coercion result that turned out to be an error code. */
export function isErrCode(v: number | string | boolean | ErrorCode): v is ErrorCode {
  return typeof v === 'string' && v.startsWith('#');
}

/* -------------------------------------------------------------- comparison */

/** Type rank for cross-type comparison: numbers < text < booleans. */
function rankOf(v: Scalar): number {
  switch (v.k) {
    case 'number':
      return 0;
    case 'text':
      return 1;
    case 'bool':
      return 2;
    default:
      return 0;
  }
}

/** A blank compared against `other` behaves as the zero value of `other`'s type. */
function fillBlank(other: Scalar): Scalar {
  switch (other.k) {
    case 'text':
      return text('');
    case 'bool':
      return FALSE;
    default:
      return { k: 'number', v: 0 };
  }
}

/**
 * Excel's total order over non-error scalars: numbers before text before
 * booleans, text compared case-insensitively. Returns a negative number, zero,
 * or a positive number.
 */
export function compareScalars(a: Scalar, b: Scalar): number {
  if (a.k === 'empty' && b.k === 'empty') return 0;
  const left = a.k === 'empty' ? fillBlank(b) : a;
  const right = b.k === 'empty' ? fillBlank(a) : b;
  const ra = rankOf(left);
  const rb = rankOf(right);
  if (ra !== rb) return ra - rb;
  if (left.k === 'number' && right.k === 'number') {
    return left.v < right.v ? -1 : left.v > right.v ? 1 : 0;
  }
  if (left.k === 'text' && right.k === 'text') {
    const x = left.v.toUpperCase();
    const y = right.v.toUpperCase();
    return x < y ? -1 : x > y ? 1 : 0;
  }
  if (left.k === 'bool' && right.k === 'bool') {
    return (left.v ? 1 : 0) - (right.v ? 1 : 0);
  }
  return 0;
}

/** Equality under {@link compareScalars}. */
export function scalarsEqual(a: Scalar, b: Scalar): boolean {
  return compareScalars(a, b) === 0;
}

/* ------------------------------------------------------------------ ranges */

/** Builds a range from two corners, normalized and clamped to the sheet limits. */
export function makeRange(sheet: SheetId, r0: number, c0: number, r1: number, c1: number): Range {
  return {
    sheet,
    r0: Math.max(0, Math.min(r0, r1)),
    c0: Math.max(0, Math.min(c0, c1)),
    r1: Math.min(MAX_ROWS - 1, Math.max(r0, r1)),
    c1: Math.min(MAX_COLS - 1, Math.max(c0, c1)),
  };
}

/** True when every corner of `r` is inside the sheet limits. */
export function rangeInBounds(r: Range): boolean {
  return r.r0 >= 0 && r.c0 >= 0 && r.r1 < MAX_ROWS && r.c1 < MAX_COLS && r.r0 <= r.r1 && r.c0 <= r.c1;
}
